import React, { useState, useEffect, useRef, useContext } from 'react';
import { MessageCircle } from 'react-feather';
import { AuthContext } from '../AuthContext';
import { Link} from 'react-router-dom';
import { useTheme } from '../ThemeContext';
// import getCrowdQuestions from '../../MongoDb/GetCrowdQuestions';
// import updateQuestionResult from '../../MongoDb/UpdateQuestionResult';
// import addNewQuestion from '../../MongoDb/AddNewQuestion';
import { debounce } from '../../MongoDb/debounce';
import { getCrowdGameSocket } from '../../utils/CrowdGameSocket';
import { url as API_BASE_URL } from "../../MongoDb/MongoConstants";
import gmsprite from '../../images/Sprite 1.png';
import sprite1 from '../../images/Sprite 2.png';
import sprite2 from '../../images/Sprite 3.png';
import sprite3 from '../../images/Sprite 4.png';
import './Crowd.css';
import '../shared/PageHeader.css';

function Crowd() {
  const { user } = useContext(AuthContext);
  const { isDarkMode } = useTheme();
  
  // Constants
  const DEFAULT_ARENA_SIZE = 800;
  const BALL_SIZE = 20;
  const MAGNET_STRENGTH = 0.01;
  const FRICTION = 0.9;

  // Function to get current arena size based on screen size
  const getArenaSize = () => {
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    
    // Calculate arena size to match CSS: min(70vh, 70vw, 800px)
    let size;
    
    if (viewportWidth <= 480) {
      // Mobile: min(40vh, 90vw, 300px)
      size = Math.min(viewportHeight * 0.4, viewportWidth * 0.9, 300);
    } else if (viewportWidth <= 768) {
      // Tablet: min(50vh, 80vw, 400px)
      size = Math.min(viewportHeight * 0.5, viewportWidth * 0.8, 400);
    } else {
      // Desktop: min(70vh, 70vw, 800px)
      size = Math.min(viewportHeight * 0.7, viewportWidth * 0.7, 800);
    }
    
    // Ensure minimum size
    return Math.max(size, 200);
  };
  
  // Function to generate random sprite position outside the arena
  const generateSpritePosition = () => {
    const crowdMainElement = document.querySelector('.crowd-main');
    const arenaElement = document.querySelector('.arena');
    
    if (!crowdMainElement || !arenaElement) {
      return { x: 100, y: 100 }; // Fallback position
    }
    
    const crowdMainRect = crowdMainElement.getBoundingClientRect();
    const arenaRect = arenaElement.getBoundingClientRect();
    
    // Calculate arena position relative to crowd-main
    const arenaRelativeX = arenaRect.left - crowdMainRect.left;
    const arenaRelativeY = arenaRect.top - crowdMainRect.top;
    
    const arenaSize = Math.min(arenaRect.width, arenaRect.height);
    const centerX = arenaRelativeX + arenaSize / 2;
    const centerY = arenaRelativeY + arenaSize / 2;
    
    // Define safe zones outside the arena
    const margin = 60; // Minimum distance from arena edge
    const safeRadius = (arenaSize / 2) + margin;
    
    let x, y;
    let attempts = 0;
    const maxAttempts = 20;
    
    do {
      // Generate random position within crowd-main bounds
      x = Math.random() * (crowdMainRect.width - 120) + 60; // 60px margin from edges
      y = Math.random() * (crowdMainRect.height - 120) + 60;
      
      // Check if position is outside the arena safe zone
      const distanceFromCenter = Math.sqrt((x - centerX) ** 2 + (y - centerY) ** 2);
      
      if (distanceFromCenter >= safeRadius) {
        break; // Found a good position
      }
      
      attempts++;
    } while (attempts < maxAttempts);
    
    // If we couldn't find a good position, place it in a corner
    if (attempts >= maxAttempts) {
      const corners = [
        { x: 60, y: 60 },
        { x: crowdMainRect.width - 120, y: 60 },
        { x: 60, y: crowdMainRect.height - 120 },
        { x: crowdMainRect.width - 120, y: crowdMainRect.height - 120 }
      ];
      const randomCorner = corners[Math.floor(Math.random() * corners.length)];
      x = randomCorner.x;
      y = randomCorner.y;
    }
    
    return { x, y };
  };

  // Function to get sprite image for a player
  const getSpriteForPlayer = (userId, isGameMaster) => {
    console.log('Getting sprite for player:', { userId, isGameMaster });
    if (isGameMaster) {
      console.log('Player is game master, returning gmsprite:', gmsprite);
      return gmsprite; // Game master gets the special sprite
    } else {
      // Regular players get random sprites
      const playerSprites = [sprite1, sprite2, sprite3];
      // Use userId to deterministically select a sprite (so it's consistent for each player)
      const spriteIndex = parseInt(userId.slice(-1), 16) % playerSprites.length;
      console.log('Player is regular player, returning sprite:', playerSprites[spriteIndex], 'at index:', spriteIndex);
      return playerSprites[spriteIndex];
    }
  };
  
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newQuestion, setNewQuestion] = useState('');
  const [gameActive, setGameActive] = useState(false);
  const [ballPosition, setBallPosition] = useState(() => {
    const size = getArenaSize();
    return { x: size * 0.2, y: size * 0.2 }; // 20% from edges instead of fixed 200px
  });
  const [ballVelocity, setBallVelocity] = useState({ x: 0, y: 0 });
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [cursorInArena, setCursorInArena] = useState(false);
  const [cursorInGameArea, setCursorInGameArea] = useState(false);
  const gameAreaRef = useRef(null);
  const gameContainerRef = useRef(null);
  const animationRef = useRef(null);
  const gameEndedRef = useRef(false);
  const [gameResult, setGameResult] = useState(null);
  const [resetCountdown, setResetCountdown] = useState(null);
  const [isResetting, setIsResetting] = useState(false);
  const [arenaSize, setArenaSize] = useState(800); // Default size, will be updated on mount
  const [updatedQuestions, setUpdatedQuestions] = useState(new Set()); // Track which questions have been updated
  const [submittingQuestion, setSubmittingQuestion] = useState(false); // Track question submission state
  const [submittedQuestions, setSubmittedQuestions] = useState(new Set()); // Track which questions have been submitted
  
  // Multiplayer states
  const [otherPlayers, setOtherPlayers] = useState(new Map()); // Track other players' positions
  const [isGameMaster, setIsGameMaster] = useState(false); // Who controls ball physics
  const [connectedPlayers, setConnectedPlayers] = useState(0);
  const [countdown, setCountdown] = useState(null); // Countdown state (3, 2, 1, or null)
  const [isCountingDown, setIsCountingDown] = useState(false); // Track if countdown is active
  const [newQuestionAlert, setNewQuestionAlert] = useState(false); // Show alert when new question arrives
  const [playerSprites, setPlayerSprites] = useState(new Map()); // Track player sprites and positions
  const socketRef = useRef(null);
  const lastBallUpdateRef = useRef(0);
  const BALL_UPDATE_THROTTLE = 16; // ~60fps

  // Get the oldest unanswered question
  const currentQuestion = Array.isArray(questions) ? questions
    .filter(q => !q.answered)
    .sort((a, b) => {
      const aTime = new Date(a.asktimestamp || a.timestamp).getTime();
      const bTime = new Date(b.asktimestamp || b.timestamp).getTime();
      return aTime - bTime;
    })[0] : null;

  const submitQuestion = async () => {
    if (!newQuestion.trim() || !user || submittingQuestion) {
      return;
    }

    const submissionKey = `${user.id || user._id}-${newQuestion.trim()}`;
    
    setSubmittingQuestion(true);
    setSubmittedQuestions(prev => new Set([...prev, submissionKey]));

    try {
      // Call the API endpoint to add the question
      const response = await fetch(`${API_BASE_URL}/api/crowd/question`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text: newQuestion.trim(),
          askedBy: user.id || user._id
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to submit question');
      }

      const result = await response.json();
      
      if (result) {
        console.log('Question submitted successfully:', result);
        
        // Add the new question to local state
        setQuestions(prev => [...prev, result]);
        
        // Broadcast the new question to other users via WebSocket
        console.log('Checking WebSocket connection:', {
          socketExists: !!socketRef.current,
          isConnected: socketRef.current?.isConnected,
          socketState: socketRef.current?.socket?.readyState
        });
        
        if (socketRef.current && socketRef.current.isConnected) {
          console.log('Broadcasting new question via WebSocket:', result);
          socketRef.current.broadcastNewQuestion(result);
          
          socketRef.current.send('new_question_lobby', {
            question: result,
            timestamp: Date.now()
          });
        } else {
          console.warn('WebSocket not connected, cannot broadcast new question');
        }
        
        setNewQuestion('');
        
        // Refresh questions from API to ensure all instances are synchronized
        setTimeout(async () => {
          try {
            const response = await fetch(`${API_BASE_URL}/api/crowd/questions`);
            if (response.ok) {
              const questionsData = await response.json();
              if (Array.isArray(questionsData)) {
                console.log('🔄 Refreshing questions after submission');
                setQuestions(questionsData);
              }
            }
          } catch (error) {
            console.error('Error refreshing questions:', error);
          }
        }, 500);
        
      } else {
        console.error('Failed to submit question');
        setSubmittedQuestions(prev => {
          const newSet = new Set(prev);
          newSet.delete(submissionKey);
          return newSet;
        });
      }
    } catch (error) {
      console.error('Error submitting question:', error);
      setSubmittedQuestions(prev => {
        const newSet = new Set(prev);
        newSet.delete(submissionKey);
        return newSet;
      });
    } finally {
      setSubmittingQuestion(false);
    }
  };

  // Create a debounced version of submitQuestion to prevent rapid submissions
  const debouncedSubmitQuestion = debounce(submitQuestion, 1000);

  // Handle Enter key press in textarea
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (!submittingQuestion && newQuestion.trim()) {
        debouncedSubmitQuestion();
      }
    }
  };

  // Initialize WebSocket connection for multiplayer
  useEffect(() => {
    if (user) {
      const socket = getCrowdGameSocket();
      socketRef.current = socket;
      
      // Generate a game ID - use a general crowd game ID if no current question
      const gameId = currentQuestion 
        ? `crowd-${currentQuestion._id || currentQuestion.id}`
        : 'crowd-lobby';
      
      // Connect to the multiplayer session
      socket.connect(user.id || user._id, gameId);

      // Set up event listeners
      const handleConnected = () => {
        console.log('Connected to multiplayer game');
      };

      const handlePlayerJoined = (data) => {
        console.log('Player joined:', data.userId, 'isGameMaster:', data.isGameMaster);
        setConnectedPlayers(data.playerCount);
        
        // Set game master status based on server response
        if (data.userId === (user.id || user._id)) {
          setIsGameMaster(data.isGameMaster);
        }
        
        // Add sprite for all players (including current user)
        setPlayerSprites(prev => {
          const newSprites = new Map(prev);
          if (!newSprites.has(data.userId)) {
            console.log('Adding sprite for player:', {
              userId: data.userId,
              username: data.username,
              isGameMaster: data.isGameMaster,
              currentUserId: user.id || user._id
            });
            
            const spriteData = {
              userId: data.userId,
              username: data.username || `Player ${data.userId.slice(-4)}`,
              position: generateSpritePosition(),
              isGameMaster: data.isGameMaster,
              sprite: getSpriteForPlayer(data.userId, data.isGameMaster),
              timestamp: Date.now()
            };
            
            console.log('Created sprite data:', spriteData);
            newSprites.set(data.userId, spriteData);
          }
          return newSprites;
        });
      };

      const handlePlayerLeft = (data) => {
        console.log('Player left:', data.userId, 'newGameMaster:', data.newGameMaster);
        setConnectedPlayers(data.playerCount);
        setOtherPlayers(prev => {
          const newPlayers = new Map(prev);
          newPlayers.delete(data.userId);
          return newPlayers;
        });
        
        // Remove sprite for leaving player
        setPlayerSprites(prev => {
          const newSprites = new Map(prev);
          newSprites.delete(data.userId);
          return newSprites;
        });
        
        // Update game master status if it changed
        if (data.newGameMaster === (user.id || user._id)) {
          setIsGameMaster(true);
        } else if (data.userId === (user.id || user._id)) {
          setIsGameMaster(false);
        }
      };

      const handlePlayerPosition = (data) => {
        if (data.userId !== (user.id || user._id)) {
          setOtherPlayers(prev => {
            const newPlayers = new Map(prev);
            newPlayers.set(data.userId, {
              position: data.position,
              timestamp: data.timestamp,
              userId: data.userId
            });
            return newPlayers;
          });
        }
      };

      const handleBallUpdate = (data) => {
        // Only non-game-masters receive ball updates
        if (!isGameMaster && data.timestamp > lastBallUpdateRef.current) {
          setBallPosition(data.position);
          setBallVelocity(data.velocity);
          lastBallUpdateRef.current = data.timestamp;
        }
      };

      const handleGameStart = () => {
        if (!isGameMaster) {
          setGameActive(true);
          setGameResult(null);
          gameEndedRef.current = false;
          setCountdown(null);
          setIsCountingDown(false);
        }
      };

      const handleGameEnd = (data) => {
        if (!isGameMaster) {
          setGameResult(data.result);
          setGameActive(false);
          gameEndedRef.current = true;
          setCountdown(null);
          setIsCountingDown(false);
          
          // Start 10-second countdown for automatic reset
          startResetCountdown();
        }
      };

      const handleCountdownStart = () => {
        console.log('🔥 COUNTDOWN START EVENT RECEIVED');
        console.log('Current isGameMaster:', isGameMaster);
        console.log('Current isCountingDown:', isCountingDown);
        console.log('Current gameActive:', gameActive);
        
        setIsCountingDown(true);
        setCountdown(3);
        console.log('✅ Countdown state updated - isCountingDown: true, countdown: 3');
      };

      const handleCountdownUpdate = (data) => {
        console.log('🔥 COUNTDOWN UPDATE EVENT RECEIVED');
        console.log('Received count:', data.count);
        console.log('Current isGameMaster:', isGameMaster);
        console.log('Current countdown:', countdown);
        
        setCountdown(data.count);
        console.log('✅ Countdown updated to:', data.count);
      };

      const handleNewQuestion = (data) => {
        console.log('🔥 NEW QUESTION EVENT RECEIVED:', data.question);
        console.log('Current questions count before:', questions.length);
        
        // Add the new question to local state
        setQuestions(prev => {
          // Check if question already exists to prevent duplicates
          const exists = prev.some(q => (q._id || q.id) === (data.question._id || data.question.id));
          console.log('Question already exists?', exists);
          
          if (!exists) {
            console.log('Adding new question to state');
            const newQuestions = [...prev, data.question];
            console.log('New questions count:', newQuestions.length);
            
            // If this was the first question (transitioning from empty state), show alert
            if (prev.length === 0) {
              setNewQuestionAlert(true);
              setTimeout(() => setNewQuestionAlert(false), 3000); // Hide after 3 seconds
            }
            
            // If this is the first question and no current question exists, 
            // the currentQuestion computed property will automatically update
            // and trigger a game session switch
            return newQuestions;
          }
          console.log('Question already exists, not adding');
          return prev;
        });
      };

      const handleQuestionUpdate = (data) => {
        console.log('🔥 QUESTION UPDATE EVENT RECEIVED:', data.questionId, data.update);
        
        // Update the specific question in local state
        setQuestions(prev => prev.map(q => {
          const questionId = q._id || q.id;
          if (questionId === data.questionId) {
            console.log('Updating question:', questionId, data.update);
            return { ...q, ...data.update };
          }
          return q;
        }));
      };

      const handleNewQuestionLobby = (data) => {
        console.log('🔥 NEW QUESTION LOBBY EVENT RECEIVED:', data.question);
        // Same handler as regular new question
        handleNewQuestion(data);
      };

      // Register event listeners
      socket.on('connected', handleConnected);
      socket.on('player_joined', handlePlayerJoined);
      socket.on('player_left', handlePlayerLeft);
      socket.on('player_position', handlePlayerPosition);
      socket.on('ball_update', handleBallUpdate);
      socket.on('game_start', handleGameStart);
      socket.on('game_end', handleGameEnd);
      socket.on('countdown_start', handleCountdownStart);
      socket.on('countdown_update', handleCountdownUpdate);
      socket.on('new_question', handleNewQuestion);
      socket.on('question_update', handleQuestionUpdate);
      socket.on('new_question_lobby', handleNewQuestionLobby);

      return () => {
        // Clean up event listeners
        socket.off('connected', handleConnected);
        socket.off('player_joined', handlePlayerJoined);
        socket.off('player_left', handlePlayerLeft);
        socket.off('player_position', handlePlayerPosition);
        socket.off('ball_update', handleBallUpdate);
        socket.off('game_start', handleGameStart);
        socket.off('game_end', handleGameEnd);
        socket.off('countdown_start', handleCountdownStart);
        socket.off('countdown_update', handleCountdownUpdate);
        socket.off('new_question', handleNewQuestion);
        socket.off('question_update', handleQuestionUpdate);
        socket.off('new_question_lobby', handleNewQuestionLobby);
        
        // Disconnect from the game
        socket.disconnect();
      };
    }
  }, [user]); // Only depend on user, not currentQuestion

  // Handle game session switching when questions change
  useEffect(() => {
    if (socketRef.current && user) {
      const newGameId = currentQuestion 
        ? `crowd-${currentQuestion._id || currentQuestion.id}`
        : 'crowd-lobby';
      
      // Reconnect to the appropriate game session
      console.log('Switching to game session:', newGameId);
      socketRef.current.connect(user.id || user._id, newGameId);
    }
  }, [currentQuestion, user]);

  const startGame = () => {
    console.log('🎮 START GAME CALLED');
    console.log('currentQuestion:', !!currentQuestion);
    console.log('gameActive:', gameActive);
    console.log('isCountingDown:', isCountingDown);
    console.log('isGameMaster:', isGameMaster);
    console.log('socketRef.current:', !!socketRef.current);
    console.log('socket isConnected:', socketRef.current?.isConnected);
    
    if (currentQuestion && !gameActive && !isCountingDown) {
      console.log('✅ Starting game countdown sequence');
      
      // Start countdown sequence
      setIsCountingDown(true);
      setCountdown(3);
      
      // Broadcast countdown start to other players
      if (isGameMaster && socketRef.current) {
        console.log('🚀 Broadcasting countdown start as game master');
        socketRef.current.send('countdown_start', { timestamp: Date.now() });
      } else {
        console.log('❌ Not broadcasting countdown start:', {
          isGameMaster,
          hasSocket: !!socketRef.current
        });
      }
      
      // Countdown from 3 to 1
      let count = 3;
      const countdownInterval = setInterval(() => {
        count--;
        console.log('⏰ Countdown tick:', count);
        setCountdown(count);
        
        // Broadcast countdown update to other players
        if (isGameMaster && socketRef.current) {
          console.log('🚀 Broadcasting countdown update:', count);
          socketRef.current.send('countdown_update', { count, timestamp: Date.now() });
        } else {
          console.log('❌ Not broadcasting countdown update:', {
            isGameMaster,
            hasSocket: !!socketRef.current,
            count
          });
        }
        
        if (count === 0) {
          clearInterval(countdownInterval);
          
          // Start the actual game after countdown
          setTimeout(() => {
            setGameActive(true);
            setBallPosition({ x: arenaSize / 2, y: arenaSize / 2 });
            setBallVelocity({ x: 0, y: 0 });
            setGameResult(null);
            setUpdatedQuestions(new Set());
            gameEndedRef.current = false;
            setCountdown(null);
            setIsCountingDown(false);
            
            // Broadcast game start to other players
            if (isGameMaster && socketRef.current) {
              socketRef.current.startGame();
            }
          }, 500); // Small delay after countdown reaches 0
        }
      }, 1000); // 1 second intervals
    }
  };

  const startResetCountdown = () => {
    setIsResetting(true);
    setResetCountdown(10);
    
    const resetInterval = setInterval(() => {
      setResetCountdown(prevCount => {
        if (prevCount <= 1) {
          clearInterval(resetInterval);
          // Reset the arena state
          setGameResult(null);
          setIsResetting(false);
          setResetCountdown(null);
          gameEndedRef.current = false;
          
          // Auto-advance to next question if available
          const nextQ = Array.isArray(questions) ? questions
            .filter(q => !q.answered)
            .sort((a, b) => {
              const aTime = new Date(a.asktimestamp || a.timestamp).getTime();
              const bTime = new Date(b.asktimestamp || b.timestamp).getTime();
              return aTime - bTime;
            })[0] : null;
          
          if (nextQ) {
            // Questions are already sorted by newest first in display,
            // but we need the oldest unanswered for next question logic
            const oldestUnanswered = Array.isArray(questions) ? questions
              .filter(q => !q.answered)
              .sort((a, b) => {
                const aTime = new Date(a.asktimestamp || a.timestamp).getTime();
                const bTime = new Date(b.asktimestamp || b.timestamp).getTime();
                return aTime - bTime; // Ascending order for next question
              })[0] : null;
            
            // No need to manually set currentQuestion since it's computed from questions array
            // The currentQuestion will automatically update when questions state changes
          }
          
          return 0;
        }
        return prevCount - 1;
      });
    }, 1000);
  };

  const handleMouseMove = (e) => {
    if (gameAreaRef.current && gameActive) {
      setCursorInGameArea(true);
      
      // Get the crowd-main div (the current target)
      const crowdMainRect = e.currentTarget.getBoundingClientRect();
      const arenaRect = gameAreaRef.current.getBoundingClientRect();
      
      // Get mouse position relative to crowd-main
      const mouseX = e.clientX - crowdMainRect.left;
      const mouseY = e.clientY - crowdMainRect.top;
      
      // Calculate arena position relative to crowd-main
      const arenaOffsetX = arenaRect.left - crowdMainRect.left;
      const arenaOffsetY = arenaRect.top - crowdMainRect.top;
      
      // Convert mouse position to arena coordinates
      const x = mouseX - arenaOffsetX;
      const y = mouseY - arenaOffsetY;
      
      const newPosition = { x, y };
      setMousePosition(newPosition);
      
      // Cursor is always in magnetic range when in crowd-main area
      setCursorInArena(true);
      
      // Broadcast position to other players
      if (socketRef.current) {
        socketRef.current.updatePlayerPosition(newPosition);
      }
    }
  };

  const handleMouseLeave = () => {
    setCursorInArena(false);
    setCursorInGameArea(false);
  };

  // Load questions from database on component mount
  useEffect(() => {
    const loadQuestions = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${API_BASE_URL}/api/crowd/questions`);
        
        if (response.ok) {
          const questionsData = await response.json();
          
          if (Array.isArray(questionsData)) {
            setQuestions(questionsData);
          } else {
            console.log('API response is not an array:', questionsData);
            setQuestions([]);
          }
        } else {
          throw new Error('Failed to fetch questions');
        }
      } catch (error) {
        console.error('Error loading questions:', error);
        setQuestions([]);
      } finally {
        setLoading(false);
      }
    };

    loadQuestions();
    
    // Set up polling for real-time updates if WebSocket is not available
    const pollInterval = setInterval(async () => {
      // Only poll if WebSocket is not connected
      if (!socketRef.current || !socketRef.current.isConnected) {
        console.log('📡 Polling for question updates (WebSocket not connected)');
        try {
          const response = await fetch(`${API_BASE_URL}/api/crowd/questions`);
          if (!response.ok) throw new Error('Failed to fetch questions');
          const questionsData = await response.json();
          if (Array.isArray(questionsData)) {
            setQuestions(prev => {
              // Only update if there are actual changes
              if (JSON.stringify(prev) !== JSON.stringify(questionsData)) {
                console.log('📡 Questions updated via polling');
                return questionsData;
              }
              return prev;
            });
          }
        } catch (error) {
          console.error('Error polling questions:', error);
        }
      }
    }, 2000); // Poll every 2 seconds

    return () => {
      clearInterval(pollInterval);
    };
  }, []);

  // Handle window resize and update arena size
  useEffect(() => {
    const handleResize = () => {
      // Get actual arena size from the DOM element
      const arenaElement = document.querySelector('.arena');
      if (arenaElement) {
        const rect = arenaElement.getBoundingClientRect();
        const newSize = Math.min(rect.width, rect.height);
        setArenaSize(newSize);
        
        // If game is active, adjust ball position proportionally
        if (gameActive && arenaSize > 0) {
          setBallPosition(prevPos => ({
            x: (prevPos.x / arenaSize) * newSize,
            y: (prevPos.y / arenaSize) * newSize
          }));
        }
        
        // Regenerate sprite positions to ensure they stay outside arena
        setPlayerSprites(prev => {
          const newSprites = new Map();
          prev.forEach((sprite, userId) => {
            newSprites.set(userId, {
              ...sprite,
              position: generateSpritePosition()
            });
          });
          return newSprites;
        });
      }
    };

    window.addEventListener('resize', handleResize);
    
    // Also update size immediately on mount
    handleResize();
    
    return () => window.removeEventListener('resize', handleResize);
  }, [arenaSize, gameActive]);
  // }, [arenaSize]);

  // Physics simulation (only game master calculates physics)
  useEffect(() => {
    if (!gameActive || !currentQuestion || gameEndedRef.current || !isGameMaster) return;

    const animate = () => {
      setBallPosition(prevPos => {
        setBallVelocity(prevVel => {
          let newVel = { ...prevVel };
          
          // Apply magnetic force from current player if cursor is in arena
          if (cursorInArena) {
            const dx = mousePosition.x - prevPos.x;
            const dy = mousePosition.y - prevPos.y;
            const distance = Math.sqrt(dx * dx + dy * dy);
            
            if (distance > 0) {
              // Reverse force calculation - stronger when further from ball
              const normalizedDistance = Math.min(distance / 100, 3); // Scale distance with max of 3
              const force = MAGNET_STRENGTH * normalizedDistance; // Multiply by distance for stronger pull when far
              newVel.x += (dx / distance) * force;
              newVel.y += (dy / distance) * force;
            }
          }
          
          // Apply magnetic forces from other players
          otherPlayers.forEach((player) => {
            const dx = player.position.x - prevPos.x;
            const dy = player.position.y - prevPos.y;
            const distance = Math.sqrt(dx * dx + dy * dy);
            
            if (distance > 0) {
              const normalizedDistance = Math.min(distance / 100, 3);
              const force = MAGNET_STRENGTH * normalizedDistance;
              newVel.x += (dx / distance) * force;
              newVel.y += (dy / distance) * force;
            }
          });
          
          // Apply friction
          newVel.x *= FRICTION;
          newVel.y *= FRICTION;
          
          return newVel;
        });
        
        const newPos = {
          x: prevPos.x + ballVelocity.x,
          y: prevPos.y + ballVelocity.y
        };
        
        // Broadcast ball position to other players (throttled)
        const now = Date.now();
        if (socketRef.current && now - lastBallUpdateRef.current > BALL_UPDATE_THROTTLE) {
          socketRef.current.updateBallPosition(newPos, ballVelocity);
          lastBallUpdateRef.current = now;
        }
        
        // Check for collision with arena walls
        const centerX = arenaSize / 2;
        const centerY = arenaSize / 2;
        const distanceFromCenter = Math.sqrt((newPos.x - centerX) ** 2 + (newPos.y - centerY) ** 2);
        
        if (distanceFromCenter >= (arenaSize / 2) - BALL_SIZE / 2) {
          // Ball hit the edge - check if it's at the goal zones (top or bottom apex)
          const angle = Math.atan2(newPos.y - centerY, newPos.x - centerX);
          const normalizedAngle = ((angle + Math.PI * 2) % (Math.PI * 2));
          
          // Define goal zones at the apex points (top and bottom)
          const goalZoneWidth = Math.PI / 6; // 30 degrees on each side of apex (60 degrees total)
          
          // Top goal zone (NO) - around angle π/2 (90 degrees, top of circle)
          const topGoalMin = (Math.PI / 2) - goalZoneWidth;
          const topGoalMax = (Math.PI / 2) + goalZoneWidth;
          
          // Bottom goal zone (YES) - around angle 3π/2 (270 degrees, bottom of circle)
          const bottomGoalMin = (3 * Math.PI / 2) - goalZoneWidth;
          const bottomGoalMax = (3 * Math.PI / 2) + goalZoneWidth;
          
          let answer = null;
          
          // Check if ball hit the top goal zone (NO)
          if (normalizedAngle >= topGoalMin && normalizedAngle <= topGoalMax) {
            answer = 'Yes';
          }
          // Check if ball hit the bottom goal zone (YES)
          else if (normalizedAngle >= bottomGoalMin && normalizedAngle <= bottomGoalMax) {
            answer = 'No';
          }
          
          if (answer) {
            // Immediately stop the game and animation
            setGameResult(answer);
            setGameActive(false);
            gameEndedRef.current = true;
            
            // Cancel the current animation frame to stop immediately
            if (animationRef.current) {
              cancelAnimationFrame(animationRef.current);
            }
            
            // Broadcast game end to other players
            if (socketRef.current) {
              socketRef.current.endGame(answer);
            }
            
            // Start 10-second countdown for automatic reset
            startResetCountdown();
            
            // Get question ID for tracking
            const questionKey = currentQuestion._id || currentQuestion.id;
            
            console.log('Answering question:', {
              questionKey,
              currentQuestion: currentQuestion.text,
              answer
            });
            
            // Only update database if we haven't already updated this question
            if (!updatedQuestions.has(questionKey)) {
              // Mark this question as being updated
              setUpdatedQuestions(prev => new Set([...prev, questionKey]));
              
              // Get array of all active users for answeredBy
              const activeUsers = Array.from(playerSprites.keys()); // Get all connected player IDs
              
              // Update the question in the database
              const updateDatabase = async () => {
                try {
                  const response = await fetch(`${API_BASE_URL}/api/crowd/question/${questionKey}/answer`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                      answer: answer,
                      answeredBy: activeUsers,
                      askedBy: user.id || user._id
                    })
                  });

                  if (!response.ok) {
                    const error = await response.json();
                    throw new Error(error.error || 'Failed to update question');
                  }
                  console.log('Question updated in database successfully');
                } catch (error) {
                  console.error('Failed to update question in database:', error);
                  // Remove from updated set if failed so it can be retried
                  setUpdatedQuestions(prev => {
                    const newSet = new Set(prev);
                    newSet.delete(questionKey);
                    return newSet;
                  });
                }
              };
              
              // Update database
              updateDatabase();
            }
            
            // Update local state - only update the specific question that was answered
            setQuestions(prev => Array.isArray(prev) ? prev.map(q => {
              // Match by _id (MongoDB) or id (local), and ensure we're updating the right question
              const questionId = q._id || q.id;
              const currentQuestionId = currentQuestion._id || currentQuestion.id;
              
              console.log('Checking question:', {
                questionId,
                currentQuestionId,
                questionText: q.text,
                isMatch: questionId === currentQuestionId
              });
              
              if (questionId === currentQuestionId) {
                console.log('Updating question with answer:', answer);
                const updatedQuestion = { ...q, answered: true, answer, answertimestamp: new Date().toISOString() };
                
                // Broadcast the question update to other users
                if (socketRef.current && socketRef.current.isConnected) {
                  socketRef.current.broadcastQuestionUpdate(questionId, {
                    answered: true,
                    answer,
                    answertimestamp: updatedQuestion.answertimestamp
                  });
                }
                
                return updatedQuestion;
              }
              return q; // Return unchanged for all other questions
            }) : []);
            
            // Refresh questions from database to ensure consistency
            setTimeout(async () => {
              try {
                const response = await fetch(`${API_BASE_URL}/api/crowd/questions`);
                if (!response.ok) throw new Error('Failed to fetch questions');
                const questionsData = await response.json();
                if (Array.isArray(questionsData)) {
                  console.log('Refreshed questions from database');
                  setQuestions(questionsData);
                }
              } catch (error) {
                console.error('Error refreshing questions after answer:', error);
              }
            }, 1500); // Delay to ensure database update is complete
            
            return prevPos; // Don't update position after game ends
          } else {
            // Ball hit the wall but not in a goal zone - keep it inside arena
            const maxDistance = (arenaSize / 2) - BALL_SIZE / 2;
            const normalX = (newPos.x - centerX) / distanceFromCenter;
            const normalY = (newPos.y - centerY) / distanceFromCenter;
            
            // Position ball at the edge but inside the arena
            return {
              x: centerX + normalX * maxDistance,
              y: centerY + normalY * maxDistance
            };
          }
        }
        
        return newPos;
      });
      
      // Stop animation immediately if game has ended
      if (gameActive && !gameEndedRef.current) {
        animationRef.current = requestAnimationFrame(animate);
      }
    };
    
    animationRef.current = requestAnimationFrame(animate);
    
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [gameActive, cursorInArena, mousePosition, ballVelocity, currentQuestion, arenaSize, isGameMaster, otherPlayers]);

  if (!user) {
    return (
      <div className="crowd-container">
        <div className="crowd-login-prompt">   
          <h2>
              Please <Link to="/login">log in</Link> to view Crowd.
          </h2>
        </div>
      </div>
    );
  }

  return (
    <div className="crowd-container">
      {/* Crowd Header */}
      <div className="top-header-header">
        <div className="top-header-title">
          <MessageCircle className="top-header-icon" />
          <div>
            <h1>Crowd</h1>
          </div>
            <span className="top-header-beta">BETA</span>

        </div>
        <p className="top-header-subtitle">
          Ask questions, play games, and make decisions together with the community!
        </p>
      </div>
      {newQuestionAlert && (
        <div className="new-question-alert">
          🎉 New question arrived! Get ready to play!
        </div>
      )}
      
      {/* Content Area with Sidebar and Main */}
      <div className="crowd-content">
        <div className="crowd-sidebar">
        <div className="question-form">
          <h3>Ask a Question</h3>
          <textarea
            className="question-input"
            value={newQuestion}
            onChange={(e) => setNewQuestion(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Type your yes/no question here..."
            maxLength={200}
            rows={3}
            disabled={submittingQuestion}
          />
          <button 
            onClick={debouncedSubmitQuestion}
            disabled={!newQuestion.trim() || submittingQuestion}
            className="submit-question-btn"
          >
            {submittingQuestion ? 'Submitting...' : 'Submit Question'}
          </button>
        </div>
        
        <div className="question-queue">
          <h3>Question Queue</h3>
          <div className="queue-list">
            {loading ? (
              <p className="loading-questions">Loading questions...</p>
            ) : !Array.isArray(questions) || questions.length === 0 ? (
              <p className="no-questions">No questions yet. Be the first to ask!</p>
            ) : (
              questions
                .sort((a, b) => {
                  const aTime = new Date(a.asktimestamp || a.timestamp).getTime();
                  const bTime = new Date(b.asktimestamp || b.timestamp).getTime();
                  return bTime - aTime; // Changed to descending order (newest first)
                })
                .map(question => (
                  <div 
                    key={question._id || question.id} 
                    className={`queue-item ${question.answered ? 'answered' : ''} ${(question._id || question.id) === (currentQuestion?._id || currentQuestion?.id) ? 'current' : ''}`}
                  >
                    <div className="question-text">{question.text}</div>
                    {question.answered && (
                      <div className={`answer-badge ${question.answer?.toLowerCase()}`}>
                        {question.answer}
                      </div>
                    )}
                    {(question._id || question.id) === (currentQuestion?._id || currentQuestion?.id) && !question.answered && (
                      <div className="current-badge">Current</div>
                    )}
                  </div>
                ))
            )}
          </div>
                      {/* Google AdSense */}
            <div className="sidebar-section">
                <div className="adsense-container">
                    <ins 
                        className="adsbygoogle"
                        style={{ 
                            display: 'block',
                            width: '80%',
                            height: '280px'
                        }}
                        data-ad-client="ca-pub-7565792583009117" // Replace with your AdSense client ID
                        data-ad-slot="8119024903"         // Replace with your ad slot ID
                        data-ad-format="auto"
                        data-full-width-responsive="true"
                    ></ins>
                </div>
            </div>
        </div>
        
      </div>
      
      <div 
        className="crowd-main"
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        style={{
          cursor: cursorInGameArea && gameActive ? 'none' : 'default'
        }}
      >
        <div 
          className={`game-area ${cursorInGameArea && gameActive ? 'magnet-cursor' : ''}`}
          ref={gameContainerRef}
        >
          {currentQuestion ? (
            <>
              <div className="current-question">
                <h2>{currentQuestion.text}</h2>
                <div className="multiplayer-status">
                  <span className="player-count">👥 {connectedPlayers} player{connectedPlayers !== 1 ? 's' : ''}</span>
                  {isGameMaster ? (
                    <span className="game-master-badge">👑 Game Host</span>
                  ) : (
                    <span className="player-badge">🎮 Player</span>
                  )}
                  <span className="player-count">
                    {socketRef.current?.isConnected ? '🟢 Live' : '🟡 Polling'}
                  </span>
                </div>
              </div>
              
              <div 
                className={`arena ${gameActive ? 'active' : ''}`}
                ref={gameAreaRef}
              >
                {/* Goal zones */}
                <div className="goal-zone no-zone"></div>
                <div className="goal-zone yes-zone"></div>
                
                <div className="arena-label no-label">NO</div>
                <div className="arena-label yes-label">YES</div>
                
                {gameActive && (
                  <div 
                    className="ball"
                    style={{
                      left: ballPosition.x - BALL_SIZE / 2,
                      top: ballPosition.y - BALL_SIZE / 2,
                      width: BALL_SIZE,
                      height: BALL_SIZE
                    }}
                  />
                )}
                
                {cursorInGameArea && gameActive && (
                  <div 
                    className="magnet-indicator player-magnet"
                    style={{
                      left: mousePosition.x - 18, // Adjusted for larger magnet
                      top: mousePosition.y - 18,  // Adjusted for larger magnet
                      transform: `rotate(${Math.atan2(
                        ballPosition.y - mousePosition.y,
                        ballPosition.x - mousePosition.x
                      ) * 180 / Math.PI + 270}deg)`
                    }}
                  >
                    🧲
                  </div>
                )}
                
                {/* Other players' magnet indicators */}
                {gameActive && Array.from(otherPlayers.values()).map((player, index) => (
                  <div 
                    key={player.userId}
                    className="magnet-indicator other-player-magnet"
                    style={{
                      left: player.position.x - 12, // Adjusted for smaller magnet
                      top: player.position.y - 12,  // Adjusted for smaller magnet
                      transform: `rotate(${Math.atan2(
                        ballPosition.y - player.position.y,
                        ballPosition.x - player.position.x
                      ) * 180 / Math.PI + 270}deg)`,
                      filter: `hue-rotate(${index * 60}deg)` // Different colors for different players
                    }}
                  >
                    🧲
                  </div>
                ))}
                
                {gameResult && (
                  <div className="game-result-overlay">
                    <div className="game-result-arena">
                      <h3>Result: <span className={gameResult.toLowerCase()}>{gameResult}</span></h3>
                      <p>The crowd has spoken!</p>
                    </div>
                  </div>
                )}
                
                {isCountingDown && countdown !== null && (
                  <div className="countdown-overlay">
                    <div className="countdown-display">
                      {countdown > 0 ? countdown : 'GO!'}
                    </div>
                  </div>
                )}
              </div>
              
              <div className="game-controls">
                {!gameActive && !gameResult && !isCountingDown && isGameMaster && (
                  <button onClick={startGame} className="start-game-btn">
                    Drop the Ball
                  </button>
                )}
                {!gameActive && !gameResult && !isCountingDown && !isGameMaster && (
                  <div className="waiting-for-game-master">
                    <p>⏳ Waiting for Game Host to start the game...</p>
                  </div>
                )}
                {isCountingDown && (
                  <div className="countdown-status">
                    <p>🎮 Game starting in {countdown > 0 ? countdown : 'GO!'}...</p>
                  </div>
                )}
                {gameResult && !isResetting && (
                  <div className="game-result-status">
                    <p>🎯 Game Complete! Auto-reset in progress...</p>
                  </div>
                )}
                {isResetting && resetCountdown !== null && (
                  <div className="reset-countdown-status">
                    <p>⏰ Next question in {resetCountdown} seconds...</p>
                  </div>
                )}
              </div>
            </>
          ) : loading ? (
            <div className="no-questions-main">
              <h2>Loading questions...</h2>
              <p>Please wait while we fetch the latest questions.</p>
              <div className="multiplayer-status">
                <span className="player-count">👥 {connectedPlayers} player{connectedPlayers !== 1 ? 's' : ''} waiting</span>
                <span className="player-count">
                  {socketRef.current?.isConnected ? '🟢 Live' : '🟡 Polling'}
                </span>
              </div>
            </div>
          ) : (
            <div className="no-questions-main">
              <h2>No questions in queue</h2>
              <p>Submit a question to get started!</p>
              <div className="multiplayer-status">
                <span className="player-count">👥 {connectedPlayers} player{connectedPlayers !== 1 ? 's' : ''} waiting</span>
                {isGameMaster ? (
                    <span className="game-master-badge">👑 Game Host</span>
                  ) : (
                    <span className="player-badge">🎮 Player</span>
                )}
                <span className="player-count">
                  {socketRef.current?.isConnected ? '🟢 Live' : '🟡 Polling'}
                </span>
              </div>
            </div>
          )}
        </div>
        
        {/* Player Sprites */}
        {Array.from(playerSprites.values()).map((player) => (
          <div
            key={player.userId}
            className="player-sprite"
            style={{
              position: 'absolute',
              left: player.position.x,
              top: player.position.y,
              zIndex: 5
            }}
          >
            <div className="player-name-tag">
              {player.username}
            </div>
            <img 
              src={player.sprite} 
              alt={`${player.username} sprite`}
              className="sprite-image"
            />
          </div>
        ))}
        </div>
      </div>
    </div>
  );
}

export default Crowd;
